import { useNode, useEditor } from "@craftjs/core";
import React, { useState } from "react";
import { ImageUpload } from "../ui/ImageUpload";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import ImageIcon from "@mui/icons-material/Image";
import { selectedOutlineSx } from "../../config/settingsStyles";

export interface ImageProps {
  src: string;
  alt: string;
  description?: string;
  width?: string;
  height?: string;
  objectFit?: "cover" | "contain" | "fill" | "none";
  borderRadius?: number;
  link?: string;
  linkType?: "external" | "internal" | "phone" | "email";
  linkTarget?: "_self" | "_blank";
  align?: "left" | "center" | "right";
}

export const Image = ({
  src,
  alt,
  width = "100%",
  height = "auto",
  objectFit = "cover",
  borderRadius = 0,
  link,
  linkType = "external",
  linkTarget = "_self",
  align = "left",
}: ImageProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { inEditor } = useEditor((state) => ({
    inEditor: true,
  }));

  const [hasError, setHasError] = useState(false);
  const hasValidSrc = src && src.trim() !== "" && !hasError;

  const getHref = () => {
    if (!link) return undefined;
    switch (linkType) {
      case "phone": return `tel:${link.replace(/\s/g, "")}`;
      case "email": return `mailto:${link}`;
      case "internal": return link.startsWith("/") ? link : `/${link}`;
      default: return link;
    }
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (inEditor) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleImageError = () => {
    setHasError(true);
  };

  const handleImageLoad = () => {
    setHasError(false);
  };

  // Placeholder when no valid image
  const placeholderElement = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width,
        height: height === "auto" ? "200px" : height,
        minHeight: "120px",
        bgcolor: "#e5e7eb",
        border: "2px dashed #9ca3af",
        borderRadius: `${borderRadius}px`,
        ...(selected && selectedOutlineSx),
      }}
    >
      <ImageIcon sx={{ fontSize: 48, color: "#6b7280", mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        Image
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Add image in settings
      </Typography>
    </Box>
  );

  const imageElement = hasValidSrc ? (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={handleImageError}
      onLoad={handleImageLoad}
      sx={{
        display: "block",
        width,
        height,
        objectFit,
        borderRadius: `${borderRadius}px`,
        ...(selected && selectedOutlineSx),
      }}
    />
  ) : (
    placeholderElement
  );

  // Alignment styles
  const alignmentSx = {
    display: "flex",
    justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
  };

  if (link) {
    return (
      <Box
        ref={(ref: HTMLDivElement | null) => {
          if (ref) connect(drag(ref));
        }}
        sx={alignmentSx}
      >
        <Box
          component="a"
          href={getHref()}
          target={linkType === "external" ? linkTarget : "_self"}
          rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
          onClick={handleLinkClick}
          sx={{ display: "block" }}
        >
          {imageElement}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={alignmentSx}
    >
      {imageElement}
    </Box>
  );
};

const SG = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
    <Box sx={{ px: 1.5, py: 1, bgcolor: "#f9fafb", borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>{children}</Box>
  </Box>
);

export const ImageSettings = () => {
  const {
    actions: { setProp },
    src,
    alt,
    description,
    width,
    height,
    objectFit,
    borderRadius,
    link,
    linkType,
    linkTarget,
    align,
  } = useNode((node) => ({
    src: node.data.props.src,
    alt: node.data.props.alt,
    description: node.data.props.description,
    width: node.data.props.width,
    height: node.data.props.height,
    objectFit: node.data.props.objectFit,
    borderRadius: node.data.props.borderRadius,
    link: node.data.props.link,
    linkType: node.data.props.linkType,
    linkTarget: node.data.props.linkTarget,
    align: node.data.props.align,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Image */}
      <SG title="Image">
        <ImageUpload value={src} onChange={(url) => setProp((props: ImageProps) => (props.src = url))} label="Image" />
        <TextField
          label="Alt Text"
          value={alt}
          onChange={(e) => setProp((props: ImageProps) => (props.alt = e.target.value))}
          placeholder="Image description"
          size="small"
          fullWidth
        />
        <TextField
          label="คำอธิบายรูป"
          value={description || ""}
          onChange={(e) => setProp((props: ImageProps) => (props.description = e.target.value || undefined))}
          placeholder="อธิบายรูปนี้ เช่น รูปแพทย์กำลังทำทรีทเมนต์"
          size="small"
          fullWidth
          multiline
          rows={2}
        />
      </SG>

      {/* Size & Layout */}
      <SG title="Size & Layout">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField
            label="Width"
            value={width}
            onChange={(e) => setProp((props: ImageProps) => (props.width = e.target.value))}
            placeholder="100% or 300px"
            size="small"
          />
          <TextField
            label="Height"
            value={height}
            onChange={(e) => setProp((props: ImageProps) => (props.height = e.target.value))}
            placeholder="auto or 200px"
            size="small"
          />
        </Box>
        <FormControl size="small" fullWidth>
          <InputLabel>Object Fit</InputLabel>
          <Select
            value={objectFit}
            label="Object Fit"
            onChange={(e) =>
              setProp((props: ImageProps) => (props.objectFit = e.target.value as ImageProps["objectFit"]))
            }
          >
            <MenuItem value="cover">Cover</MenuItem>
            <MenuItem value="contain">Contain</MenuItem>
            <MenuItem value="fill">Fill</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </Select>
        </FormControl>
        <Box>
          <Typography variant="body2" gutterBottom>
            Alignment
          </Typography>
          <ToggleButtonGroup
            value={align || "left"}
            exclusive
            onChange={(_, value) => {
              if (value !== null) {
                setProp((props: ImageProps) => (props.align = value as ImageProps["align"]));
              }
            }}
            size="small"
            fullWidth
          >
            <ToggleButton value="left" sx={{ flex: 1 }}>
              <FormatAlignLeftIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="center" sx={{ flex: 1 }}>
              <FormatAlignCenterIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="right" sx={{ flex: 1 }}>
              <FormatAlignRightIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </SG>

      {/* Style */}
      <SG title="Style">
        <Box>
          <Typography variant="body2" gutterBottom>
            Border Radius: {borderRadius}px
          </Typography>
          <Slider
            value={borderRadius}
            onChange={(_, value) => setProp((props: ImageProps) => (props.borderRadius = value as number))}
            min={0}
            max={50}
            valueLabelDisplay="auto"
            size="small"
          />
        </Box>
      </SG>

      {/* Link */}
      <SG title="Link">
        <FormControl size="small" fullWidth>
          <InputLabel>Link Type</InputLabel>
          <Select
            value={linkType || "external"}
            label="Link Type"
            onChange={(e) => setProp((props: ImageProps) => (props.linkType = e.target.value as ImageProps["linkType"]))}
          >
            <MenuItem value="external">External URL</MenuItem>
            <MenuItem value="internal">Internal Page</MenuItem>
            <MenuItem value="phone">Phone Number</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label={linkType === "phone" ? "Phone Number" : linkType === "email" ? "Email Address" : "URL"}
          value={link || ""}
          onChange={(e) => setProp((props: ImageProps) => (props.link = e.target.value || undefined))}
          placeholder={linkType === "phone" ? "02-XXX-XXXX" : linkType === "email" ? "email@example.com" : "https://"}
          size="small"
          fullWidth
        />
        {linkType === "external" && link && (
          <FormControl size="small" fullWidth>
            <InputLabel>Target</InputLabel>
            <Select
              value={linkTarget}
              label="Target"
              onChange={(e) => setProp((props: ImageProps) => (props.linkTarget = e.target.value as "_self" | "_blank"))}
            >
              <MenuItem value="_self">Same Window</MenuItem>
              <MenuItem value="_blank">New Window</MenuItem>
            </Select>
          </FormControl>
        )}
      </SG>
    </Box>
  );
};

Image.craft = {
  displayName: "Image",
  props: {
    src: "",
    alt: "Image",
    description: "",
    width: "100%",
    height: "auto",
    objectFit: "cover",
    borderRadius: 0,
    link: undefined,
    linkType: "external",
    linkTarget: "_self",
    align: "left",
  },
  related: {
    settings: ImageSettings,
  },
};
